import React, { useMemo } from 'react';

import useCurrentTextStyle from '@src/hooks/useCurrentTextStyle';
import useMediaQuery from '@src/hooks/useMediaQuery';
import useShoppingBasketCart from '@src/hooks/useShoppingBasketCart';

import { MinicartTotals, BNPLSection } from '../../Minicart/MinicartBlocks/MinicartTotals';
import { Button } from '@src/stories/elements/Button/Button';
import { MinicartDiscounts } from '../../Minicart/MinicartBlocks/MinicartDiscounts';

export const CartSummary = ({ settings }) => {
  const { getCurrentTextStyle } = useCurrentTextStyle();

  // Get mobile breakpoint for padding calculations
  const isMobile = useMediaQuery(null, 767);

  // Extract block settings from the settings prop
  const blockSettings = settings.block_settings || {};

  const { cartData, isGlobalCartLoading } = useShoppingBasketCart();

  // Extract settings that match the cart-summary.liquid schema
  const {
    sticky_on_mobile,

    // Header Settings (from schema)
    heading = 'Order Summary',
    heading_style = 'b1',
    heading_style_mobile,
    heading_style_tablet,
    heading_style_ultrawide,
    heading_style_enable_responsive,
    heading_text_color,
    header_padding_top = 0,
    header_padding_bottom = 0,
    header_padding_left = 0,
    header_padding_right = 0,
    header_padding_top_mobile = 0,
    header_padding_bottom_mobile = 0,
    header_padding_left_mobile = 0,
    header_padding_right_mobile = 0,

    // Content Settings (from schema)
    content_padding_top = 0,
    content_padding_bottom = 0,
    content_padding_left = 0,
    content_padding_right = 0,
    content_padding_top_mobile = 0,
    content_padding_bottom_mobile = 0,
    content_padding_left_mobile = 0,
    content_padding_right_mobile = 0,

    checkout_button_text = 'CHECKOUT',
    checkout_button_style = 'secondary',
    checkout_button_size = 'lg'
  } = blockSettings;

  // Header padding styles
  const headerPaddingStyle = useMemo(
    () => ({
      paddingTop: `${isMobile ? header_padding_top_mobile : header_padding_top}px`,
      paddingBottom: `${isMobile ? header_padding_bottom_mobile : header_padding_bottom}px`,
      paddingLeft: `${isMobile ? header_padding_left_mobile : header_padding_left}px`,
      paddingRight: `${isMobile ? header_padding_right_mobile : header_padding_right}px`
    }),
    [
      isMobile,
      header_padding_top,
      header_padding_bottom,
      header_padding_left,
      header_padding_right,
      header_padding_top_mobile,
      header_padding_bottom_mobile,
      header_padding_left_mobile,
      header_padding_right_mobile
    ]
  );

  // Content padding styles
  const contentPaddingStyle = useMemo(
    () => ({
      paddingTop: `${isMobile ? content_padding_top_mobile : content_padding_top}px`,
      paddingBottom: `${isMobile ? content_padding_bottom_mobile : content_padding_bottom}px`,
      paddingLeft: `${isMobile ? content_padding_left_mobile : content_padding_left}px`,
      paddingRight: `${isMobile ? content_padding_right_mobile : content_padding_right}px`
    }),
    [
      isMobile,
      content_padding_top,
      content_padding_bottom,
      content_padding_left,
      content_padding_right,
      content_padding_top_mobile,
      content_padding_bottom_mobile,
      content_padding_left_mobile,
      content_padding_right_mobile
    ]
  );

  return (
    <div
      className={`cart__summary overflow-hidden ${
        sticky_on_mobile
          ? 'max-md:fixed left-0 bottom-0 z-10 w-full bg-white shadow-[1px_-3px_20px_0_rgba(0,_0,_0,_0.05)]'
          : ''
      }`}
    >
      {/* Header container with border but no padding */}
      <div className='cart__summary-header border-b border-[#EDEDED]'>
        {/* Inner header with custom padding */}
        <div
          className='cart__summary-header-content flex items-center justify-start gap-2'
          style={headerPaddingStyle}
        >
          {heading && (
            <h3
              className={`m-0 ${getCurrentTextStyle(
                heading_style,
                heading_style_enable_responsive,
                heading_style_mobile,
                heading_style_tablet,
                heading_style_ultrawide
              )}`}
              style={{ color: heading_text_color }}
            >
              {heading}
            </h3>
          )}
        </div>
      </div>

      {/* Content container with custom padding */}
      <div style={contentPaddingStyle}>
          {/* Always show MinicartDiscounts, even if cart is empty, but only on desktop */}
          {!isMobile && (
            <div className={`${cartData?.items && cartData.items.length > 0 ? 'mb-6' : 'mb-[24px]'}`}>
              <MinicartDiscounts
                cart_subtotal={cartData?.items_subtotal_price ? cartData.items_subtotal_price / 100 : 0}
                settings={blockSettings}
              />
            </div>
          )}
        {cartData?.items &&
          cartData.items.length > 0 &&
          cartData.items_subtotal_price &&
          cartData.items_subtotal_price > 0 && (
            <>

              <div className='pt-4 pb-3 lg:py-4'>
                <MinicartTotals
                  cart_subtotal={cartData.items_subtotal_price ? cartData.items_subtotal_price / 100 : 0}
                  cart_items={cartData.items || []}
                  cart={cartData}
                  type='cart'
                  settings={blockSettings}
                />
              </div>
            </>
          )}

        <Button
          style={checkout_button_style}
          size={checkout_button_size}
          className={`cart__summary-checkout-button ${cartData?.items && cartData.items.length > 0 ? 'lg:mt-6' : ''}  w-full`}
          label={checkout_button_text}
          disabled={!cartData || cartData.item_count === 0 || isGlobalCartLoading}
          link='/checkout'
        />

        {/* Buy Now Pay Later - Only show in cart */}
        <BNPLSection settings={blockSettings} />
      </div>
    </div>
  );
};
